
import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ref, onValue, query, orderByChild, equalTo } from 'firebase/database';
import { db } from '../firebase';
import { Product } from '../types';
import { useApp } from '../App';
import { ChevronRight, Filter, ShoppingCart, SlidersHorizontal } from 'lucide-react';

const CategoryPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const { categories, addToCart } = useApp();
  
  const currentCategory = categories.find(c => c.slug === slug);

  useEffect(() => {
    setLoading(true);
    if (slug) {
      const productsRef = query(
        ref(db, 'products'),
        orderByChild('category'),
        equalTo(slug)
      );
      
      onValue(productsRef, (snapshot) => {
        const data = snapshot.val();
        if (data) {
          setProducts(Object.values(data));
        } else {
          setProducts([]);
        }
        setLoading(false);
      });
    }
  }, [slug]);

  return (
    <div className="container mx-auto px-4 md:px-6 pb-20">
      {/* Breadcrumb */}
      <nav className="flex items-center space-x-2 text-sm text-stone-500 mb-8">
        <Link to="/" className="hover:text-amber-800">Ana Sayfa</Link>
        <ChevronRight size={14} />
        <span className="text-stone-900 font-medium capitalize">{currentCategory?.name || slug}</span>
      </nav>

      <div className="flex flex-col md:flex-row gap-12">
        {/* Sidebar Filters */}
        <aside className="w-full md:w-72 space-y-8">
          <div className="bg-white p-8 rounded-[2.5rem] border border-stone-100 shadow-sm">
            <h3 className="font-serif font-bold text-stone-900 text-xl mb-8 flex items-center">
              <Filter size={20} className="mr-3 text-amber-700" /> Filtrele
            </h3>
            
            <div className="space-y-8">
              <div>
                <p className="text-[10px] font-black text-stone-400 uppercase tracking-widest mb-4 ml-1">Koleksiyonlar</p>
                <div className="space-y-3">
                  {categories.map(cat => (
                    <Link 
                      key={cat.id} 
                      to={`/category/${cat.slug}`}
                      className={`block text-sm font-medium transition-colors ${cat.slug === slug ? 'text-amber-800 font-bold' : 'text-stone-500 hover:text-amber-700'}`}
                    >
                      {cat.name}
                    </Link>
                  ))}
                </div>
              </div>

              <div>
                <p className="text-[10px] font-black text-stone-400 uppercase tracking-widest mb-4 ml-1">Fiyat Aralığı</p>
                <div className="space-y-4">
                  <div className="flex items-center space-x-2">
                    <input type="number" placeholder="Min" className="w-full bg-stone-50 border border-stone-100 rounded-xl py-2 px-3 text-sm focus:ring-2 focus:ring-amber-500 outline-none text-stone-900 placeholder-stone-400" />
                    <span className="text-stone-300">-</span>
                    <input type="number" placeholder="Max" className="w-full bg-stone-50 border border-stone-100 rounded-xl py-2 px-3 text-sm focus:ring-2 focus:ring-amber-500 outline-none text-stone-900 placeholder-stone-400" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </aside>

        {/* Product Grid */}
        <div className="flex-1">
          <div className="flex items-center justify-between mb-10">
            <h1 className="text-3xl font-serif font-bold text-stone-900 capitalize">
              {currentCategory?.name || slug} <span className="ml-3 text-sm font-medium text-stone-400 italic font-sans lowercase">({products.length} Ürün)</span>
            </h1>
            <div className="flex items-center space-x-4">
              <button className="flex items-center space-x-2 text-xs font-bold text-stone-600 bg-white px-5 py-2.5 rounded-xl border border-stone-100 hover:bg-stone-50 uppercase tracking-widest">
                <SlidersHorizontal size={16} />
                <span>Sıralama</span>
              </button>
            </div>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3, 4, 5, 6].map(i => (
                <div key={i} className="bg-white rounded-[2.5rem] border border-stone-100 h-[28rem] animate-pulse"></div>
              ))}
            </div>
          ) : products.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {products.map((p) => (
                <div key={p.id} className="group bg-white rounded-[2.5rem] border border-stone-100 overflow-hidden hover:shadow-2xl transition-all flex flex-col">
                  <Link to={`/product/${p.id}`} className="block relative aspect-[4/5] bg-stone-50 overflow-hidden">
                    {p.isNew && (
                      <span className="absolute top-6 left-6 z-10 bg-amber-800 text-white text-[10px] font-black uppercase tracking-widest px-3 py-1.5 rounded-full">Yeni</span>
                    )}
                    <img 
                      src={(p.images && p.images.length > 0) ? p.images[0] : 'https://images.unsplash.com/photo-1603006905003-be475563bc59?auto=format&fit=crop&q=80&w=500'} 
                      alt={p.name} 
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" 
                    />
                  </Link>
                  <div className="p-8 flex-1 flex flex-col">
                    <div className="text-[10px] font-black text-amber-700 uppercase tracking-widest mb-2">{p.brand || "Lumina Home"}</div>
                    <Link to={`/product/${p.id}`} className="font-serif font-bold text-lg text-stone-900 hover:text-amber-800 mb-4 line-clamp-1 transition-colors">
                      {p.name}
                    </Link>
                    <div className="mt-auto flex items-center justify-between">
                      <div className="flex flex-col">
                        {p.oldPrice && <span className="text-xs text-stone-400 line-through">₺{p.oldPrice.toLocaleString()}</span>}
                        <span className="text-xl font-black text-stone-900">₺{p.price.toLocaleString()}</span>
                      </div>
                      <button 
                        onClick={() => addToCart(p)}
                        className="w-12 h-12 bg-stone-900 text-white rounded-2xl flex items-center justify-center hover:bg-amber-800 active:scale-95 transition-all shadow-xl shadow-stone-900/10"
                      >
                        <ShoppingCart size={20} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-white rounded-[3rem] border border-stone-100 p-24 text-center">
              <p className="text-stone-400 font-medium italic">Bu kategoride henüz bir çalışma bulunmuyor.</p>
              <Link to="/" className="mt-6 inline-block text-amber-800 font-bold hover:underline uppercase tracking-widest text-xs">Keşfetmeye Başla</Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CategoryPage;
